import { Account } from '@core/model/domain/account';
import { ValidCaseState } from '@core/model/domain/caseState';
import { Bank } from '@modules/process/models/bank';
import { ConnectionFormItemDto } from '@modules/process/pages/login/services/dtos/loginDtos';

export enum ProcessEventType {
  CONSENT,
  BANK_CHOICE,
  SCA,
  GO_BACK_TO_BANK_CHOICE,
  SCA_FAILED,
  TRIALS_EXHAUSTED,
  ACCOUNTS_FOUND,
  CONTINUE_AFTER_ERROR,
  SELECTED_ACCOUNTS,
  ABANDON,
  CONTINUE,
  INTERNAL_ERROR,
  WINDOW_RESIZE_EVENT,
  WAIT_ON_EXTERNAL_BANK_LOGIN,
  RETRY_WEBAUTH,
  ASK_TO_RETRY_WEBAUTH,
  LOGIN_WEBAUTH_WITH_FORM,
}
export interface ConsentEvent {
  type: ProcessEventType.CONSENT;
}

export interface BankChoiceEvent {
  type: ProcessEventType.BANK_CHOICE;
  data: Bank;
}

export interface ScaEvent {
  type: ProcessEventType.SCA;
}

export interface GoBackToBankChoiceEvent {
  type: ProcessEventType.GO_BACK_TO_BANK_CHOICE;
}

export interface ScaFailedEvent {
  type: ProcessEventType.SCA_FAILED;
}

export interface TrialsExhaustedEvent {
  type: ProcessEventType.TRIALS_EXHAUSTED;
}

export interface AccountsFoundEvent {
  type: ProcessEventType.ACCOUNTS_FOUND;
  data: Account[];
}

export interface ContinueAfterErrorEvent {
  type: ProcessEventType.CONTINUE_AFTER_ERROR;
}
export interface AbandonEvent {
  type: ProcessEventType.ABANDON;
}

export interface SelectAccountsEvent {
  type: ProcessEventType.SELECTED_ACCOUNTS;
  data: Account[];
  status: ValidCaseState;
  taggingEventOrigin: 'result_page' | 'bank_detail';
}

export interface ContinueEvent {
  type: ProcessEventType.CONTINUE;
}

export interface InternalErrorEvent {
  type: ProcessEventType.INTERNAL_ERROR;
  payload: {
    errorOrigin: string;
    error: unknown;
  };
}

export interface WindowResizeEvent {
  type: ProcessEventType.WINDOW_RESIZE_EVENT;
  data: string;
}

export interface WaitOnExternalBankLoginEvent {
  type: ProcessEventType.WAIT_ON_EXTERNAL_BANK_LOGIN;
}

export interface AskToRetryWebauth {
  type: ProcessEventType.ASK_TO_RETRY_WEBAUTH;
}

export interface LoginWebauthWithForm {
  type: ProcessEventType.LOGIN_WEBAUTH_WITH_FORM;
  form: ConnectionFormItemDto[];
}

export type AcceptedProcessEvent =
  | ConsentEvent
  | BankChoiceEvent
  | ScaEvent
  | GoBackToBankChoiceEvent
  | ScaFailedEvent
  | TrialsExhaustedEvent
  | AccountsFoundEvent
  | SelectAccountsEvent
  | WaitOnExternalBankLoginEvent
  | AskToRetryWebauth
  | LoginWebauthWithForm;

export type ParentProcessEvent =
  | AbandonEvent
  | ContinueAfterErrorEvent
  | ContinueEvent
  | InternalErrorEvent
  | WindowResizeEvent;

export class ProcessEventFactory {
  public static SpawnConsentEvent(): ConsentEvent {
    return { type: ProcessEventType.CONSENT };
  }

  public static SpawnBankChoiceEvent(bank: Bank): BankChoiceEvent {
    return { type: ProcessEventType.BANK_CHOICE, data: bank };
  }

  public static SpawnScaEvent(): ScaEvent {
    return { type: ProcessEventType.SCA };
  }

  public static SpawnGoBackToBankChoiceEvent(): GoBackToBankChoiceEvent {
    return { type: ProcessEventType.GO_BACK_TO_BANK_CHOICE };
  }

  public static SpawnScaFailedEvent(): ScaFailedEvent {
    return { type: ProcessEventType.SCA_FAILED };
  }

  public static SpawnTrialsExhaustedEvent(): TrialsExhaustedEvent {
    return { type: ProcessEventType.TRIALS_EXHAUSTED };
  }

  public static SpawnAccountsFoundEvent(accounts: Account[]): AccountsFoundEvent {
    return { type: ProcessEventType.ACCOUNTS_FOUND, data: accounts };
  }

  public static SpawnContinueAfterErrorEvent(): ContinueAfterErrorEvent {
    return { type: ProcessEventType.CONTINUE_AFTER_ERROR };
  }

  public static SpawnAbandonEvent(): AbandonEvent {
    return { type: ProcessEventType.ABANDON };
  }

  public static SpawnSelectedAccountsEvent(
    status: ValidCaseState,
    accounts: Account[],
    taggingEventOrigin: 'result_page' | 'bank_detail'
  ): SelectAccountsEvent {
    return { type: ProcessEventType.SELECTED_ACCOUNTS, status, data: accounts, taggingEventOrigin };
  }

  public static SpawnContinueEvent(): ContinueEvent {
    return { type: ProcessEventType.CONTINUE };
  }

  public static SpawnInternalErrorEvent(payload: {
    errorOrigin: string;
    error: unknown;
  }): InternalErrorEvent {
    return { type: ProcessEventType.INTERNAL_ERROR, payload };
  }

  public static SpawnWindowResizeEvent(size: string): WindowResizeEvent {
    return { type: ProcessEventType.WINDOW_RESIZE_EVENT, data: size };
  }

  public static SpawnWaitOnExternalBankLoginEvent(): WaitOnExternalBankLoginEvent {
    return { type: ProcessEventType.WAIT_ON_EXTERNAL_BANK_LOGIN };
  }

  public static SpawnAskToRetryWebauth(): AskToRetryWebauth {
    return { type: ProcessEventType.ASK_TO_RETRY_WEBAUTH };
  }

  public static SpawnLoginWebauthWithForm(form: ConnectionFormItemDto[]): LoginWebauthWithForm {
    return { type: ProcessEventType.LOGIN_WEBAUTH_WITH_FORM, form };
  }

  public static SpawnSelectBankChoice(): ConsentEvent {
    return { type: ProcessEventType.CONSENT };
  }
}
